import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { TrendingUp, Users, BarChart3 } from 'lucide-react';

export function SimulateurExpert() {
  const [adhesion, setAdhesion] = useState('individuel'); // individuel, bureau, premium
  const [packDossiers, setPackDossiers] = useState('50');
  const [nbInterventions, setNbInterventions] = useState(20);
  const [tarifMoyen, setTarifMoyen] = useState(200);

  const adhesions = {
    individuel: { prix: 180, label: 'Expert individuel' },
    bureau: { prix: 450, label: 'Bureau d\'études' },
    premium: { prix: 750, label: 'Expert Premium' }
  };

  const packsDossiers = {
    '10': { prix: 40, dossiers: 10, coutUnitaire: 4 },
    '50': { prix: 150, dossiers: 50, coutUnitaire: 3 },
    '200': { prix: 400, dossiers: 200, coutUnitaire: 2 }
  };

  const calculs = useMemo(() => {
    const cotisationAnnuelle = adhesions[adhesion].prix;
    const pack = packsDossiers[packDossiers];
    
    // Nombre de packs nécessaires pour l'année
    const packsNecessaires = Math.ceil(nbInterventions / pack.dossiers);
    const coutDossiers = packsNecessaires * pack.prix;
    
    // Revenus bruts
    const revenusBruts = nbInterventions * tarifMoyen;
    
    // Part expert (80%)
    const partExpert = revenusBruts * 0.80;
    
    // Part O'SCOP (20%)
    const partOSCOP = revenusBruts * 0.20;
    
    // Charges totales
    const chargesTotal = cotisationAnnuelle + coutDossiers;
    
    // Revenu net
    const revenuNet = partExpert - chargesTotal;
    
    // Revenu par intervention
    const revenuParIntervention = nbInterventions > 0 ? (revenuNet / nbInterventions).toFixed(2) : 0;
    
    // Marge nette
    const margeNette = revenusBruts > 0 ? ((revenuNet / revenusBruts) * 100).toFixed(1) : 0;

    return {
      cotisationAnnuelle,
      coutDossiers,
      packsNecessaires,
      revenusBruts,
      partExpert,
      partOSCOP,
      chargesTotal,
      revenuNet,
      revenuParIntervention,
      margeNette
    };
  }, [adhesion, packDossiers, nbInterventions, tarifMoyen]);

  return (
    <Card className="border-2 border-purple-200">
      <CardHeader className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-t-lg">
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="w-6 h-6" />
          Simulateur Expert - Calculez vos revenus
        </CardTitle>
        <CardDescription className="text-purple-100">
          Estimez vos revenus annuels en tant qu'expert O'SCOP
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Paramètres */}
          <div className="space-y-5">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              Configurez votre profil
            </h4>

            {/* Type d'adhésion */}
            <div>
              <Label className="text-sm font-medium">Type d'adhésion</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {Object.entries(adhesions).map(([key, value]) => (
                  <button
                    key={key}
                    onClick={() => setAdhesion(key)}
                    className={`p-3 rounded-lg border-2 text-center transition-all ${
                      adhesion === key 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="text-xs text-gray-600">{value.label}</p>
                    <p className="font-bold text-purple-600">{value.prix}€/an</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Pack dossiers */}
            <div>
              <Label className="text-sm font-medium">Pack crédits-dossiers</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {Object.entries(packsDossiers).map(([key, value]) => (
                  <button
                    key={key}
                    onClick={() => setPackDossiers(key)}
                    className={`p-3 rounded-lg border-2 text-center transition-all ${
                      packDossiers === key 
                        ? 'border-amber-500 bg-amber-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="text-xs text-gray-600">Pack {value.dossiers}</p>
                    <p className="font-bold text-amber-600">{value.prix}€</p>
                    <p className="text-xs text-gray-500">{value.coutUnitaire}€/dossier</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Nombre d'interventions */}
            <div>
              <Label className="text-sm font-medium">
                Interventions prévues / an : <span className="text-purple-600 font-bold">{nbInterventions}</span>
              </Label>
              <Slider
                value={[nbInterventions]}
                onValueChange={(v) => setNbInterventions(v[0])}
                min={1}
                max={200}
                step={1}
                className="mt-3"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>1</span>
                <span>200</span>
              </div>
            </div>

            {/* Tarif moyen */}
            <div>
              <Label className="text-sm font-medium">
                Tarif moyen / intervention : <span className="text-purple-600 font-bold">{tarifMoyen}€</span>
              </Label>
              <Slider
                value={[tarifMoyen]}
                onValueChange={(v) => setTarifMoyen(v[0])}
                min={50}
                max={500}
                step={10}
                className="mt-3"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>50€</span>
                <span>500€</span>
              </div>
            </div>
          </div>

          {/* Résultats */}
          <div className="space-y-4">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Projection annuelle
            </h4>

            {/* Tableau récapitulatif */}
            <div className="bg-gray-50 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b border-gray-200">
                    <td className="p-3 text-gray-600">Chiffre d'affaires brut</td>
                    <td className="p-3 text-right font-bold">{calculs.revenusBruts.toLocaleString()}€</td>
                  </tr>
                  <tr className="border-b border-gray-200 bg-green-50">
                    <td className="p-3 text-gray-600">Votre part (80%)</td>
                    <td className="p-3 text-right font-bold text-green-600">{calculs.partExpert.toLocaleString()}€</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="p-3 text-gray-600">Part O'SCOP (20%)</td>
                    <td className="p-3 text-right text-gray-500">{calculs.partOSCOP.toLocaleString()}€</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="p-3 text-gray-600">Cotisation annuelle</td>
                    <td className="p-3 text-right text-red-500">-{calculs.cotisationAnnuelle}€</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="p-3 text-gray-600">
                      Crédits-dossiers ({calculs.packsNecessaires} pack{calculs.packsNecessaires > 1 ? 's' : ''})
                    </td>
                    <td className="p-3 text-right text-red-500">-{calculs.coutDossiers}€</td>
                  </tr>
                  <tr className="bg-purple-600">
                    <td className="p-3 font-semibold text-white">REVENU NET ANNUEL</td>
                    <td className="p-3 text-right font-bold text-2xl text-white">
                      {calculs.revenuNet.toLocaleString()}€
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 gap-3">
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-3 text-center">
                  <p className="text-xs text-gray-600">Revenu / intervention</p>
                  <p className="text-xl font-bold text-blue-600">{calculs.revenuParIntervention}€</p>
                </CardContent>
              </Card>
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-3 text-center">
                  <p className="text-xs text-gray-600">Marge nette</p>
                  <p className="text-xl font-bold text-green-600">{calculs.margeNette}%</p>
                </CardContent>
              </Card>
            </div>

            {/* Mensuel */}
            <Card className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white">
              <CardContent className="p-4 text-center">
                <p className="text-sm text-purple-100">Revenu mensuel estimé</p>
                <p className="text-3xl font-bold">{Math.round(calculs.revenuNet / 12).toLocaleString()}€</p>
                <p className="text-xs text-purple-200 mt-1">
                  Soit ~{Math.round(nbInterventions / 12)} interventions/mois
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
